<template>
    <div class="TicketListPage">
        <DashboardSectionHeader title="Tickets">
            <Button to="/company/tickets/create" class="primary">Add Ticket</Button>
        </DashboardSectionHeader>

        <DashboardFilterHeader type="ticket" @change="handleFilterChange"/>

        <DashboardList
            :columns="['Ticket #', 'Driver', 'Ticket Date', 'Client', 'Project', 'Loads']"
            :isLoading="isFetching"
            :isComplete="isComplete"
            @loadMore="handleLoadMore"
        >
            <DashboardListItem
                v-for="ticket in tickets"
                :key="ticket.id"
                :columns="[
                    ticket.ticketNumber,
                    `${ticket.user.firstName} ${ticket.user.lastName}`,
                    formatDate(ticket.submittedAt),
                    ticket.client.name,
                    ticket.project.name,
                    ticket.loads.length,
                ]"
                :actions="[
                    { label: 'Edit', click: () => $router.push(`/company/tickets/${ticket.id}/edit`) },
                    { label: 'Duplicate', click: () => handleDuplicate(ticket) },
                    { label: 'Generate Report', click: () => $router.push(`/company/reports/view?type=hauling&ticket=${ticket.ticketNumber}`) },
                    { label: 'Delete', variant: 'danger', click: () => handleDelete(ticket) },
                ]"
            />
        </DashboardList>
    </div>
</template>

<script>
import _ from 'lodash';
import * as api from '@/api';
import { formatDate } from '@/utils/formatting';
import DashboardSectionHeader from '@/components/DashboardSectionHeader';
import DashboardList from '@/components/DashboardList';
import DashboardListItem from '@/components/DashboardListItem';
import ConfirmModal from '@/components/modals/ConfirmModal';
import DashboardFilterHeader from '@/components/DashboardFilterHeader';

export default {
    name: 'TicketListPage',

    components: {
        DashboardSectionHeader,
        DashboardFilterHeader,
        DashboardList,
        DashboardListItem,
    },

    data: () => ({
        tickets: [],
        page: 0,
        isFetching: false,
        isComplete: false,

        filters: null,
    }),

    mounted() {
        this.fetchTickets();
    },

    methods: {
        formatDate,

        async fetchTickets(shouldResetResults = false) {
            if (this.isFetching) return;

            // Restart pagination
            if (shouldResetResults) {
                this.page = 0;
            }

            const companyId = this.$store.getters['auth/company'].id;
            const limit = 100;
            const offset = limit * this.page;

            this.isFetching = true;
            const res = await api.getTickets({
                ...(this.filters ?? {}),
                company: companyId,
                limit,
                offset,
                withUser: true
            });
            this.isFetching = false;

            if (res.error) {
                return this.$awn.alert('Failed to load tickets');
            }

            this.isComplete = res.body.length < limit;

            // Avoid appending to the list if we're filtering
            if (shouldResetResults) {
                this.tickets = res.body;
            } else {
                this.tickets = this.tickets.concat(res.body);
            }
        },

        handleLoadMore() {
            this.page += 1;
            this.fetchTickets();
        },

        handleDuplicate(originalTicket) {
            const title = 'Duplicate Confirmation';
            const message = `Are you sure you want to duplicate ticket #${originalTicket.ticketNumber}? Any signatures and images won't be copied.`;

            const ticket = _.pick(originalTicket, [
                'submittedAt',
                'project',
                'user',
                'truck',
                'trailer',
                'odometerPrecheck',
                'precheckStartAt',
                'yardLeaveAt',
                'yardArriveAt',
                'postcheckEndAt',
                'odometerPostcheck',
                'notes',
            ]);

            const loads = originalTicket.loads.map((load) => {
                return _.pick(load, [
                    'loadSiteArriveAt',
                    'loadSiteLeaveAt',
                    'dumpSiteArriveAt',
                    'dumpSiteLeaveAt',
                    'standbyStartAt',
                    'standbyEndAt',
                    'tons',
                    'material',
                    'scaleTicketCode',
                    'isBackhaul',
                    'notes',
                ]);
            });

            ticket.loads = loads;
            ticket.company = originalTicket.company?.id;
            ticket.project = originalTicket.project?.id;
            ticket.trailer = originalTicket.trailer?.id;
            ticket.truck = originalTicket.truck?.id;
            ticket.user = originalTicket.user?.id;
            ticket.submittedAt = new Date().toISOString();

            const onConfirm = async () => {
                const res = await api.createTicket(ticket);
                if (res.error) {
                    return this.$awn.alert('Failed to duplicate ticket');
                }

                this.$awn.success('Ticket duplicated');

                // TODO: Update state with new ticket. API response does not
                // come back with full object with relations included
                setTimeout(() => window.location.reload(), 500);
            };

            this.$modal.show(
                ConfirmModal,
                { title, message, type: 'info', confirmLabel: 'Duplicate', onConfirm },
                { height: 'auto' },
            );
        },

        handleDelete(ticket) {
            const title = 'Delete Confirmation';
            const message = 'Are you sure you want to delete this ticket?';

            const onConfirm = async () => {
                const res = await api.softDeleteTicket(ticket);
                if (res.error) {
                    return this.$awn.alert('Failed to delete ticket');
                }

                this.$awn.success('Ticket has been removed');
                this.tickets = this.tickets.filter(t => t.id !== ticket.id);
            };

            this.$modal.show(
                ConfirmModal,
                { title, message, type: 'delete', confirmLabel: 'Delete', onConfirm },
                { height: 'auto' },
            );
        },

        async handleFilterChange(filters) {
            this.filters = filters;
            await this.fetchTickets(true);
        },
    },
}
</script>
